import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../../shared";
import type { ApiToken } from "../../../../../../store/admin/sessionsStore";

interface GenerateTokenModalProps {
  themeClasses: ThemeClasses;
  token: ApiToken;
  onClose: () => void;
}

export default function GenerateTokenModal({
  themeClasses,
  token,
  onClose,
}: GenerateTokenModalProps) {
  const { t } = useTranslation("adminSections");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token.data.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const handleCopyAndClose = async () => {
    await handleCopy();
    setTimeout(() => onClose(), 300);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${themeClasses.bg} rounded-xl w-full max-w-lg border ${themeClasses.border} shadow-2xl`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-4">
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>
            {t("sessions.apiToken.modalTitle")}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.textSecondary}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-5 space-y-4">
          {/* Success Alert */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
              <div>
                <p className="font-semibold text-primary-800 text-sm">
                  {t("sessions.apiToken.successTitle")}
                </p>
                <p className="text-primary-700 text-sm mt-0.5">
                  {t("sessions.apiToken.successMessage")}
                </p>
              </div>
            </div>
          </div>

          {/* Token Field */}
          <div>
            <label className={`text-sm font-medium ${themeClasses.text} mb-2 block`}>
              Token API:
            </label>
            <div className="flex items-center gap-2">
              <div
                className={`flex-1 px-3 py-2.5 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.input} font-mono text-sm ${themeClasses.text} truncate`}
              >
                {token.data.token}
              </div>
              <button
                onClick={handleCopy}
                className={`p-2.5 rounded-lg transition-colors ${
                  copied
                    ? "bg-primary-500 text-white"
                    : "bg-primary-500 hover:bg-[#5ed83a] text-black"
                }`}
                title={t("sessions.apiToken.copy")}
              >
                <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-lg`}></i>
              </button>
            </div>
          </div>

          {/* Warning Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-semibold text-amber-800 text-sm">
                  {t("sessions.apiToken.importantInfo")}
                </p>
                <ul className="text-amber-700 text-sm mt-2 space-y-1">
                  <li>• {t("sessions.apiToken.infoPoint1")}</li>
                  <li>• {t("sessions.apiToken.infoPoint2")}</li>
                  <li>• {t("sessions.apiToken.infoPoint3")}</li>
                  <li>• {t("sessions.apiToken.infoPoint4")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className={`px-6 py-2.5 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} hover:bg-gray-100 transition-colors font-medium`}
          >
            {t("sessions.apiToken.close")}
          </button>
          <button
            onClick={handleCopyAndClose}
            className="px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-[#5ed83a] text-black font-medium transition-colors"
          >
            {t("sessions.apiToken.copyAndClose")}
          </button>
        </div>
      </div>
    </div>
  );
}

